import { useMutation, useQuery } from '@apollo/client'

import { gql } from '@/clients/utoniq-core'
import {
  BlockUserMutation,
  BlockUserMutationVariables,
  CurrentUserFragmentDoc,
  DeletePostReplyMutation,
  DeletePostReplyMutationVariables,
  LikePostMutation,
  LikePostMutationVariables,
  LikePostReplyMutation,
  LikePostReplyMutationVariables,
  PostBoxFragmentDoc,
  PostDetailQuery,
  PostDetailQueryVariables,
  RepliesConnectionFragmentDoc,
  ReplyBoxFragmentDoc,
  ReplyPostMutation,
  ReplyPostMutationVariables,
  UnlikePostMutation,
  UnlikePostMutationVariables,
  UnlikePostReplyMutation,
  UnlikePostReplyMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { openReportForm } from '@/core/googleForm'
import { individualFilterBannedUserPostComment } from '@/core/individual'
import { Share } from '@/core/native'
import { useCreateShortLink } from '@/lib/url/shortLink'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } = useRouter<{ postId: string }>()
  const { t: tcommon } = useTranslation()
  const { showError } = useMessage()

  const { data, loading, fetchMore, refetch } = useQuery<
    PostDetailQuery,
    PostDetailQueryVariables
  >(QUERY, {
    variables: { postId: router.query.postId },
    returnPartialData: true,
  })
  const [likePost] = useMutation<LikePostMutation, LikePostMutationVariables>(
    LIKE_POST
  )
  const [unlikePost] = useMutation<
    UnlikePostMutation,
    UnlikePostMutationVariables
  >(UNLIKE_POST)
  const [replyPost] = useMutation<
    ReplyPostMutation,
    ReplyPostMutationVariables
  >(REPLY_POST)
  const [deletePostReply] = useMutation<
    DeletePostReplyMutation,
    DeletePostReplyMutationVariables
  >(DELETE_POST_REPLY)
  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BLOCK_USER)
  const [likePostReply] = useMutation<
    LikePostReplyMutation,
    LikePostReplyMutationVariables
  >(LIKE_POST_REPLY)
  const [unlikePostReply] = useMutation<
    UnlikePostReplyMutation,
    UnlikePostReplyMutationVariables
  >(UNLIKE_POST_REPLY)

  const getShortLinkUrl = useCreateShortLink({
    path: generatePath(routerPages.PostDetail, {
      postId: router.query.postId,
    }),
  })

  return {
    state: {
      loading: useInitialLoading(data, loading),
      isLoggedIn: !!user,
      hasNextPage: !!data?.post?.replies?.pageInfo?.hasNextPage,
      post: individualFilterBannedUserPostComment(data?.post) ?? null,
      user: data?.user ?? null,
      isMembershipPersonalInformationRequired:
        !!data?.user?.isMembershipPersonalInformationRequired,
      disableReplies:
        !!data?.post?.artist.settings?.features?.post?.disableReplies,
      disableLikes: !!data?.post?.artist.settings?.features?.post?.disableLikes,
    },
    actions: {
      async onReply({ content }) {
        await replyPost({
          variables: {
            replyPostInputs: {
              postId: router.query.postId,
              content,
            },
          },
        })
      },
      async onLike(isLike, likeCount) {
        if (isLike) {
          await unlikePost({
            variables: { unlikePostPostId: router.query.postId },
            optimisticResponse: ({ unlikePostPostId }) => ({
              unlikePost: {
                __typename: 'Post',
                id: unlikePostPostId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likePost({
          variables: { likePostPostId: router.query.postId },
          optimisticResponse: ({ likePostPostId }) => ({
            likePost: {
              __typename: 'Post',
              id: likePostPostId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
      async onDeleteReply(replyId) {
        await deletePostReply({
          variables: {
            deletePostReplyId: replyId,
          },
        })
      },
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: user?.email ?? '',
          contentType: 'post-replies',
          contentId,
          userId,
          accountId,
        })
      },
      async onReportArtist(contentId, userId, accountId) {
        await openReportForm({
          email: user?.email ?? '',
          contentType: 'post',
          contentId,
          userId,
          accountId,
        })
      },
      async onBlockUser(id) {
        await blockUser({ variables: { id: id } })
        await refetch({ postId: router.query.postId })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            postId: router.query.postId,
            pageInput: {
              cursor: data?.post?.replies?.pageInfo.endCursor || null,
              limit: 30,
            },
          },
        })
      },
      async onLikeOrUnlikeReply({ commentId, isLike, likeCount }) {
        if (isLike) {
          await unlikePostReply({
            variables: { id: commentId },
            optimisticResponse() {
              return {
                unlikePostReply: {
                  id: commentId,
                  isLike: !isLike,
                  likeCount: --likeCount,
                  __typename: 'PostReply',
                },
              }
            },
          })
          return
        }
        await likePostReply({
          variables: { id: commentId },
          optimisticResponse() {
            return {
              likePostReply: {
                id: commentId,
                isLike: !isLike,
                likeCount: ++likeCount,
                __typename: 'PostReply',
              },
            }
          },
        })
      },
      async onClickShare() {
        const title = data?.post?.artist.displayName
        const url = await getShortLinkUrl()
        await Share.share({
          title: title ?? tcommon('metaTitle'),
          text: data?.post?.content ?? tcommon('metaDescription'),
          url,
          dialogTitle: title ?? tcommon('metaTitle'),
        }).catch(e => showError(e))

        return
      },
    },
  }
})

const QUERY = gql`
  query PostDetail($postId: ID!, $pageInput: PageInput) {
    post(id: $postId) {
      ...PostBox
      replies(pageInput: $pageInput) {
        ...RepliesConnection
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${CurrentUserFragmentDoc}
  ${RepliesConnectionFragmentDoc}
  ${PostBoxFragmentDoc}
`

const LIKE_POST = gql`
  mutation LikePost($likePostPostId: ID!) {
    likePost(postId: $likePostPostId) {
      id
      likeCount
      isLike
    }
  }
`

const UNLIKE_POST = gql`
  mutation UnlikePost($unlikePostPostId: ID!) {
    unlikePost(postId: $unlikePostPostId) {
      id
      likeCount
      isLike
    }
  }
`

const REPLY_POST = gql`
  mutation ReplyPost($replyPostInputs: ReplyPostInput!) {
    replyPost(inputs: $replyPostInputs) {
      ...ReplyBox
    }
  }
  ${ReplyBoxFragmentDoc}
`

const DELETE_POST_REPLY = gql`
  mutation DeletePostReply($deletePostReplyId: ID!) {
    deletePostReply(id: $deletePostReplyId) {
      ...ReplyBox
    }
  }
  ${ReplyBoxFragmentDoc}
`

const BLOCK_USER = gql`
  mutation BlockUser($id: ID!) {
    blockUser(id: $id) {
      id
      accountId
      formattedAccountId @client
      blockedUsers
    }
  }
`

const LIKE_POST_REPLY = gql`
  mutation likePostReply($id: ID!) {
    likePostReply(id: $id) {
      id
      isLike
      likeCount
    }
  }
`

const UNLIKE_POST_REPLY = gql`
  mutation unlikePostReply($id: ID!) {
    unlikePostReply(id: $id) {
      id
      isLike
      likeCount
    }
  }
`
